import React, { useState, useEffect, ReactText } from "react";
import Axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import Button from "../../Components/Button/Button";
import Modal from "../../Components/Modal/Modal";
import useWindowDimensions from "../../utils/WIndowDimensions";
import { formatAddress } from "../../utils/formatter";

import "../../css/Admin.scss";
import "react-toastify/dist/ReactToastify.css";

/**
 * Helper to convert a Blob into a base64 string
 */
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("FileReader did not return a string"));
      }
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(blob);
  });
};

const Admin = ({ email }) => {
  const [submission, setSubmission] = useState<any>({ images: 0, mailouts: 0 });
  const [images, setImages] = useState<any>([]);
  const [mailouts, setMailouts] = useState<any>([]);
  const [selectedPatronid, setSelectedPatronid] = useState("");
  const [showModal, setShowModal] = useState(false);
  const { width } = useWindowDimensions();

  // Change this to your actual background-removal base URL
  let baseURL = "/background-removal-server";

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      // 1. Get the counts
      await Axios.all([
        Axios.get(
          `https://icatcard.ucmerced.edu/auth/caticlick/mysql-get-images-row.php`
        ),
        Axios.get(
          `https://icatcard.ucmerced.edu/auth/caticlick/mysql-get-mailouts-row.php`
        ),
      ]).then(
        Axios.spread((imagesCount, mailoutsCount) => {
          setSubmission({
            images: imagesCount.data,
            mailouts: mailoutsCount.data,
          });
        })
      );

      // 2. Get the actual image records
      const limitation = 25;
      await Axios.all([
        Axios.get(
          `https://icatcard.ucmerced.edu/auth/caticlick/mysql-get-images.php?limit=${limitation}`
        ),
        Axios.get(
          `https://icatcard.ucmerced.edu/auth/caticlick/mysql-get-mailouts.php?limit=${limitation}`
        ),
      ]).then(
        Axios.spread((CatCardIds, GovernmentIds) => {
          // Keep copy of original image so we can revert
          const imagesWithBGState = CatCardIds.data.map((img: any) => ({
            ...img,
            originalSrc: img.src,
            backgroundRemoved: false,
          }));

          setImages(imagesWithBGState);
          setMailouts(GovernmentIds.data);
        })
      );
    };

    fetchData();
  }, []);

  const haveNotPrintedCatCard = (): ReactText => {
    return toast.info(
      (): JSX.Element => (
        <div
          className="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3"
          role="alert"
        >
          <p className="font-bold">
            The patron you are trying to approve already has a flag 7, and 8 or
            9. Please remove it or print their current approved CatCard ID
            Photo.
          </p>
          <footer className="flex justify-center">Tap to close.</footer>
        </div>
      ),
      {
        position: "top-right",
        autoClose: 10000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      }
    );
  };

  /**
   * Approve logic
   */
  const approve = (
    patronid,
    image,
    studentid,
    name,
    streetAddress,
    city,
    state,
    zipcode,
    uploadDate,
    dimensions,
    size,
    checkbox
  ): void => {
    // 'image' is now a base64 string after background removal,
    // or the original remote URL if user didn't remove the background.
    //
    // The server expects to store this final 'image' data.
    Axios.get(
      `https://icatcard.ucmerced.edu/auth/caticlick/oci-insert-flag.php?patronid=${patronid}&flag=7`
    )
      .then(() => {
        // Now upload/approve the final image
        return Axios.post(
          `https://icatcard.ucmerced.edu/auth/caticlick/oci-update-image.php`,
          {
            image: image, // <--- if base64, the server must handle it.
            patronid: patronid,
          },
          {
            headers: {
              "Content-Type": "text/html",
            },
            timeout: 30000,
          }
        );
      })
      .then(() => {
        return Axios.get(
          `https://icatcard.ucmerced.edu/auth/caticlick/mysql-delete-image.php?patronid=${patronid}`
        );
      })
      .then((): void => {
        removeImageFromObject(patronid);
      })
      .then(() => {
        return Axios.get(
          `https://icatcard.ucmerced.edu/auth/caticlick/oci-insert-flag.php?patronid=${patronid}&flag=${Number(
            checkbox
          )}`
        );
      })
      .then(() => {
        return Axios.get(
          `https://icatcard.ucmerced.edu/auth/caticlick/mysql-delete-mail.php?patronid=${patronid}`
        );
      })
      .then(() => {
        setSubmission((old) => {
          return { images: submission.images - 1, ...old };
        });
        return Axios.get(
          `https://icatcard.ucmerced.edu/auth/caticlick/oci-insert-log.php?patronid=${~~patronid}&studentid=${studentid}&ucmnetid=${
            email.split("@")[0]
          }&name=${name}&streetAddress=${encodeURIComponent(
            streetAddress
          )}&city=${city}&state=${state}&zipcode=${zipcode}&uploadDate=${uploadDate}&dimensions=${dimensions}&size=${size}&delivery=${~~checkbox}&approvedBy=${
            email.split("@")[0]
          }`
        );
      })
      .catch((_error) => {
        haveNotPrintedCatCard();
      });
  };

  /**
   * Decline logic
   */
  const decline = (patronid, flag, uploadDate): void => {
    Axios.get(
      `https://icatcard.ucmerced.edu/auth/caticlick/oci-insert-flag.php?patronid=${patronid}&flag=${flag}`
    )
      .then(() => {
        return Axios.get(
          `https://icatcard.ucmerced.edu/auth/caticlick/oci-delete-flag.php?patronid=${patronid}`
        );
      })
      .then(() => {
        return Axios.get(
          `https://icatcard.ucmerced.edu/auth/caticlick/mysql-delete-image.php?patronid=${patronid}`
        );
      })
      .then(() => {
        removeImageFromObject(patronid);
      })
      .then(() => {
        return Axios.get(
          `https://icatcard.ucmerced.edu/auth/caticlick/mysql-delete-mail.php?patronid=${patronid}`
        );
      })
      .then(() => {
        setSubmission((old) => {
          return { images: submission.images - 1, ...old };
        });
        return Axios.get(
          `https://icatcard.ucmerced.edu/auth/caticlick/mysql-insert-decline.php?patronid=${patronid}&flag=${flag}&uploadDate=${uploadDate}&whoDeclined=${
            email.split("@")[0]
          }`
        );
      });
  };

  /**
   * Approve all images in the current list
   */
  const approveAll = (): void => {
    images.forEach((patron) => {
      const {
        patronid,
        src,
        uploadDate,
        dimensions,
        size,
        checkbox,
        studentid,
        name,
      } = patron;

      let streetAddress = null,
        city = null,
        state = null,
        zipcode = null;

      // Check if there's a corresponding mailout
      const mailout: any = mailouts.find((p) => p["patronid"] === patronid);
      if (mailout) {
        streetAddress = mailout.streetAddress;
        city = mailout.city;
        state = mailout.state;
        zipcode = mailout.zipcode;
      }

      const photoSize: string = size.slice(0, -3);

      approve(
        patronid,
        src,
        studentid,
        name,
        streetAddress,
        city,
        state,
        zipcode,
        uploadDate,
        dimensions,
        `${photoSize}k`,
        checkbox
      );
    });
  };

  /**
   * Removes a single patron from the images array
   */
  const removeImageFromObject = (patronid): void => {
    setImages((imgs) => imgs.filter((p) => p["patronid"] !== patronid));
  };

  /**
   * Remove background logic (calls your API endpoint)
   *
   * Now, after we get the returned Blob from your server,
   * we convert it to base64 and store that in `src`.
   * This ensures that when we "Approve," we actually send
   * the new image data (base64) to the server.
   */
  const removeBackground = async (patronid: string, src: string) => {
    try {
      if (!src) {
        console.error("No image source found");
        return;
      }

      // 1) Fetch the existing image (URL from DB) as a Blob.
      //    This is necessary because we only have a URL, not a File.
      const imageResponse = await Axios.get(src, { responseType: "blob" });
      const imageBlob = imageResponse.data;

      // 2) Create a FormData object with the Blob
      const formData = new FormData();
      formData.append("image", imageBlob, "patron_image.jpg");

      // 3) POST to your remove-background endpoint, expecting a Blob in response.
      const apiResponse = await Axios.post(
        `${baseURL}/remove-background`,
        formData,
        {
          responseType: "blob", // important so Axios returns the raw binary data
        }
      );

      // 4) Convert returned Blob to a base64 string
      const newBase64 = await blobToBase64(apiResponse.data);

      // 5) Update this patron in state with the background-removed image in base64 form
      setImages((prev) =>
        prev.map((p) =>
          p.patronid === patronid
            ? {
                ...p,
                src: newBase64, // store final image as base64
                backgroundRemoved: true,
              }
            : p
        )
      );

      console.log(`Background removed for Patron ID: ${patronid}`);
    } catch (error) {
      console.error("Error removing background:", error);
    }
  };

  /**
   * Revert background for a specific patron
   *
   * We revert to their original URL from the DB.
   * That means if you "Approve" after reverting, you'll be
   * approving the original remote image. If you want to also
   * convert the original to base64, you'd do that in your fetch step,
   * but for now this reverts to the raw URL that came from DB.
   */
  const revertBackground = (patronid: string) => {
    setImages((prev) =>
      prev.map((p) =>
        p.patronid === patronid && p.backgroundRemoved
          ? {
              ...p,
              src: p.originalSrc, // revert to the original DB URL
              backgroundRemoved: false,
            }
          : p
      )
    );
    console.log(`Reverted background for Patron ID: ${patronid}`);
  };

  /**
   * Render the list of patrons/images
   */
  const generateImages = (): JSX.Element[] => {
    return images.map((patron) => {
      const {
        patronid,
        src,
        uploadDate,
        dimensions,
        size,
        checkbox,
        studentid,
        name,
        backgroundRemoved,
      } = patron;

      const mailout = mailouts.find((p) => p.patronid === patronid);

      let streetAddress: string | null = null,
        city: string | null = null,
        state: string | null = null,
        zipcode: string | null = null;

      if (mailout) {
        streetAddress = mailout.streetAddress;
        city = mailout.city;
        state = mailout.state;
        zipcode = mailout.zipcode;
      }

      const photoSize: string = size.slice(0, -3);

      return (
        <div key={`${patronid}-${uploadDate}`} className="py-4 m-5">
          <p className="justify-left align-left items-left">
            <b>{name} | </b>
            {studentid}
          </p>
          <h6>Submitted on {uploadDate.slice(5)}</h6>
          <div className="flex flex-row">
            <p>{dimensions}</p>&nbsp; | &nbsp;<p>{photoSize}k</p>
          </div>

          {/*
            If `src` is a base64 string, it will display normally in an <img> tag.
            If it's still the remote URL, it will also display.
          */}
          <img
            src={src}
            alt={`${patronid}'s id profile`}
            width="300"
            height="300"
          />

          {/* Approve Button */}
          <Button
            onClick={() => {
              approve(
                patronid,
                src, // <-- either a remote URL or the base64 string
                studentid,
                name,
                streetAddress,
                city,
                state,
                zipcode,
                uploadDate,
                dimensions,
                `${photoSize}k`,
                checkbox
              );
            }}
          >
            Approve
          </Button>

          {/* Remove BG or Revert */}
          {backgroundRemoved ? (
            <Button onClick={() => revertBackground(patronid)}>
              Revert BG
            </Button>
          ) : (
            <Button onClick={() => removeBackground(patronid, src)}>
              Remove BG
            </Button>
          )}

          {/* Decline dropdown */}
          <div className="dropdown inline-block relative ml-2">
            <button className="bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded inline-flex items-center">
              <span className="mr-1">Decline</span>
              <svg
                className="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </button>
            <ul className="dropdown-menu absolute hidden text-gray-700 pt-1">
              <li
                onClick={() => {
                  decline(patronid, 921, uploadDate);
                }}
              >
                <span className="rounded-t bg-gray-200 hover:bg-gray-400 py-2 px-4 block whitespace-no-wrap">
                  No hats/glasses/obstructions allowed
                </span>
              </li>
              <li
                onClick={() => {
                  decline(patronid, 922, uploadDate);
                }}
              >
                <span className="bg-gray-200 hover:bg-gray-400 py-2 px-4 block whitespace-no-wrap">
                  Bad crop, head not centered
                </span>
              </li>
              <li
                onClick={() => {
                  decline(patronid, 923, uploadDate);
                }}
              >
                <span className="rounded-b bg-gray-200 hover:bg-gray-400 py-2 px-4 block whitespace-no-wrap">
                  Bad angle or tilt
                </span>
              </li>
              <li
                onClick={() => {
                  decline(patronid, 924, uploadDate);
                }}
              >
                <span className="rounded-b bg-gray-200 hover:bg-gray-400 py-2 px-4 block whitespace-no-wrap">
                  Poor lighting or quality
                </span>
              </li>
              <li
                onClick={() => {
                  decline(patronid, 925, uploadDate);
                }}
              >
                <span className="rounded-b bg-gray-200 hover:bg-gray-400 py-2 px-4 block whitespace-no-wrap">
                  Facial expression/background unacceptable
                </span>
              </li>
              <li
                onClick={() => {
                  decline(patronid, 926, uploadDate);
                }}
              >
                <span className="rounded-b bg-gray-200 hover:bg-gray-400 py-2 px-4 block whitespace-no-wrap">
                  Verification error/unacceptable
                </span>
              </li>
            </ul>
          </div>

          {/* Verification Button (only if address info exists) */}
          {streetAddress && city && state && zipcode ? (
            <Button
              onClick={() => {
                setShowModal(true);
                setSelectedPatronid(patronid);
              }}
            >
              Verification
            </Button>
          ) : null}
        </div>
      );
    });
  };

  /**
   * Show the selected patron’s Government ID data
   */
  const getGovernmentId = () => {
    return mailouts
      .filter((patron) => patron.patronid === selectedPatronid)
      .map((patron) => {
        const {
          patronid,
          src,
          streetAddress,
          city,
          state,
          zipcode,
          uploadDate,
          name,
          studentid,
        } = patron;
        return (
          <React.Fragment key={patronid}>
            <p className="justify-left align-left items-left">
              <b>{name} | </b>
              {studentid}
            </p>
            <h6>Submitted on {uploadDate.slice(5)}</h6>
            <div className="flex flex-col">
              <p>{formatAddress(streetAddress)}</p>
              <p>
                {city}, {state}, {zipcode}
              </p>
            </div>
            <br />
            {width < 500 ? (
              <img
                src={src}
                alt={`${patronid}'s id profile`}
                width="300"
                height="300"
              />
            ) : (
              <img src={src} alt={`${patronid}'s id profile`} />
            )}
          </React.Fragment>
        );
      });
  };

  return (
    <main className="admin">
      <header className="flex flex-col -my-12">
        <img
          src="http://catcard.ucmerced.edu/sites/catcard.ucmerced.edu/files/documents/cat-card-logo.png"
          alt="CatCard Logo"
        />
      </header>

      <section className="flex flex-col justify-center align-center items-center">
        <h6 className="mb-4">
          Photo submission: {images.length}/{submission.images}
        </h6>
        <div className="flex-row">
          <Button onClick={approveAll}>Approve All</Button>
          &nbsp;&nbsp;
          <Button
            onClick={() => {
              window.location.replace("https://catcard.ucmerced.edu");
            }}
          >
            Logout
          </Button>
        </div>

        {images && images.length > 0 ? (
          <div className="flex flex-row flex-wrap justify-center items-center">
            {generateImages()}
          </div>
        ) : (
          <p>No pending images.</p>
        )}
      </section>

      {/* Modal for showing Government ID info */}
      <Modal
        show={showModal}
        handleClose={() => {
          setShowModal(false);
        }}
      >
        <br />
        {getGovernmentId()}
        <br />
      </Modal>

      <ToastContainer closeButton={false} />
    </main>
  );
};

export default Admin;
